import { useEffect, useRef, useState } from "react";
import { UploadCloud, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { api, API, errorMessage } from "@/lib/api";

export default function AdminGallery() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("caption", caption);
      try {
        await api.post("/admin/gallery", fd);
        toast.success(t("admin.gallery_uploaded"));
      } catch (e) {
        toast.error(errorMessage(e, t));
      }
    }
    setCaption("");
    setBusy(false);
    if (ref.current) ref.current.value = "";
    load();
  };

  const del = async (id) => {
    if (!window.confirm(t("admin.delete_confirm"))) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
    } catch (e) {
      toast.error(errorMessage(e, t));
    }
    load();
  };

  return (
    <div className="max-w-5xl" data-testid="admin-gallery-page">
      <h1 className="font-display text-3xl sm:text-4xl">{t("admin.gallery")}</h1>
      <p className="mt-3 max-w-2xl text-slate-400">{t("admin.gallery_sub")}</p>
      <div className="panel mt-8 flex flex-col gap-4 p-6 sm:flex-row sm:items-end">
        <label className="flex-1"><span className="label">{t("admin.gallery_caption")}</span><input value={caption} onChange={(e) => setCaption(e.target.value)} className="field" data-testid="gallery-caption-input" /></label>
        <input ref={ref} type="file" multiple accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm" className="hidden" onChange={(e) => upload([...e.target.files])} data-testid="gallery-file-input" />
        <button onClick={() => ref.current?.click()} disabled={busy} className="btn-gold" data-testid="gallery-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} {t("admin.gallery_add")}
        </button>
      </div>
      {items.length === 0 ? (
        <p className="mt-10 text-slate-500" data-testid="gallery-empty">{t("admin.gallery_empty")}</p>
      ) : (
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3">
          {items.map((it, i) => (
            <figure key={it.id} className="panel group relative overflow-hidden" data-testid={`gallery-item-${i}`}>
              <div className="aspect-square bg-ink">
                {it.kind === "video" ? <video src={`${API}/gallery/${it.id}/file`} className="h-full w-full object-cover" muted loop playsInline autoPlay /> : <img src={`${API}/gallery/${it.id}/file`} alt={it.caption} className="h-full w-full object-cover" />}
              </div>
              {it.caption && <figcaption className="p-3 text-sm">{it.caption}</figcaption>}
              <button onClick={() => del(it.id)} className="absolute right-2 top-2 rounded bg-ink/80 p-2 text-slate-300 hover:text-ruby" data-testid={`gallery-delete-${i}`} aria-label={t("admin.delete")}><Trash2 className="h-4 w-4" /></button>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}
